import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Loader2, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { getAssistantCapabilities, queryAssistant } from '../services/assistantApi';
import dektalMascot from '../assets/dektal-mascot-transparent.png';

const SUPPORTED_QUESTIONS = [
  {
    intent: 'today_revenue',
    label: 'Revenue today',
    examples: ['How much revenue did I make today?', 'What is today collection?'],
  },
  {
    intent: 'patient_next_appointment',
    label: 'Next appointment',
    examples: ['When is Darshan next appointment?', 'Show Patient X next appointment.'],
  },
  {
    intent: 'patient_last_visit',
    label: 'Last patient visit',
    examples: ['What did we do for Darshan last time?', 'Show Patient X last visit.'],
  },
  {
    intent: 'low_stock',
    label: 'Low stock',
    examples: ['Which consumables are low?', 'What needs restocking?'],
  },
  {
    intent: 'consultant_balance',
    label: 'Consultant dues',
    examples: ['How much do I need to pay Dr Mehta?', 'Check consultant balance for Dr A.'],
  },
  {
    intent: 'monthly_attendance',
    label: 'Attendance',
    examples: ['Who was absent this month?', 'Show staff attendance this month.'],
  },
  {
    intent: 'monthly_report_status',
    label: 'Monthly report status',
    examples: ['Is monthly report ready?', 'Show monthly report status.'],
  },
  {
    intent: 'overdue_emi',
    label: 'Overdue EMI',
    examples: ['Which EMI payments are overdue?', 'Show pending EMI due.'],
  },
  {
    intent: 'software_help',
    label: 'How-To Help',
    examples: ['How do I create an invoice?', 'How does Inventory Audit work?'],
  },
];

const HELP_STARTER_QUESTIONS = [
  'How do I record an EMI payment?',
  'How do I add a visiting consultant?',
  'Where can I see my Referral Credits?',
  'How do I unlock an additional chair?',
];

const MONTH_NAME_TO_NUMBER = {
  january: 1,
  jan: 1,
  february: 2,
  feb: 2,
  march: 3,
  mar: 3,
  april: 4,
  apr: 4,
  may: 5,
  june: 6,
  jun: 6,
  july: 7,
  jul: 7,
  august: 8,
  aug: 8,
  september: 9,
  sep: 9,
  sept: 9,
  october: 10,
  oct: 10,
  november: 11,
  nov: 11,
  december: 12,
  dec: 12,
};

function getIntentLabel(intent) {
  const match = SUPPORTED_QUESTIONS.find((entry) => entry.intent === intent);
  return match?.label || intent;
}

function buildInitialMonthYear() {
  const now = new Date();
  return {
    month: now.getMonth() + 1,
    year: now.getFullYear(),
  };
}

function buildWelcomeMessage() {
  return {
    id: `dektal-welcome-${Date.now()}`,
    sender: 'dektal',
    text: 'Hi, I am Dektal. Ask a clinic question, or ask how to use any Dektal page.',
    summary: 'Try: How do I create an invoice?',
    actionButtons: [],
    multipleMatches: [],
    answerStatus: 'answered',
    createdAt: new Date().toISOString(),
  };
}

function normalizeQuestionText(value) {
  return String(value || '')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeForMatch(value) {
  return normalizeQuestionText(value)
    .toLowerCase()
    .replace(/[^a-z0-9.\s'-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function cleanExtractedEntity(value) {
  return normalizeQuestionText(value)
    .replace(/^patient\s+/i, '')
    .replace(/\b(please|pls|kindly|today|tomorrow|now|this month|for me|can you|could you|show|check|find)\b/gi, ' ')
    .replace(/\b(next appointment|appointment|last visit|last time|patient history|history|visit|balance|fees|dues)\b/gi, ' ')
    .replace(/\b(how much|do i|still need|need to|have to|pay|owe|to)\b/gi, ' ')
    .replace(/'s\b/gi, '')
    .replace(/[?!.:,;]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractWithPatterns(question, patterns) {
  for (const pattern of patterns) {
    const match = question.match(pattern);
    if (!match?.[1]) continue;
    const cleaned = cleanExtractedEntity(match[1]);
    if (cleaned.length >= 2) {
      return cleaned;
    }
  }
  return '';
}

function extractPatientSearch(question, intent) {
  const normalized = normalizeQuestionText(question);
  if (intent === 'patient_next_appointment') {
    return extractWithPatterns(normalized, [
      /when\s+(?:is|are)\s+(.+?)\s+(?:'s\s+)?(?:next\s+)?appointment/i,
      /(?:next|upcoming)\s+appointment\s+(?:for|of)\s+(.+)/i,
      /appointment\s+(?:for|of)\s+(.+)/i,
      /patient\s+(.+?)\s+(?:next\s+)?appointment/i,
    ]);
  }

  if (intent === 'patient_last_visit') {
    return extractWithPatterns(normalized, [
      /what\s+did\s+we\s+do\s+(?:for\s+)?(.+?)\s+(?:last\s+time|last\s+visit)/i,
      /(?:last\s+visit|last\s+time|patient\s+history|history)\s+(?:for|of)\s+(.+)/i,
      /(.+?)\s+(?:'s\s+)?(?:last\s+visit|last\s+time|patient\s+history)/i,
      /patient\s+(.+?)\s+(?:last\s+visit|last\s+time|history)/i,
    ]);
  }

  return '';
}

function extractConsultantSearch(question) {
  const normalized = normalizeQuestionText(question);
  return extractWithPatterns(normalized, [
    /(?:pay|owe|dues|balance|fees).*?\b((?:dr\.?|doctor)\s+[a-z][a-z .'-]*)/i,
    /(?:consultant\s+(?:balance|fees|dues)\s+(?:for|of)?\s*)(.+)/i,
    /(?:how much.*?(?:pay|owe)\s+)(.+)/i,
    /(?:check|show|find)\s+(?:balance|dues|fees)\s+(?:for|of)\s+(.+)/i,
  ]);
}

function getMonthYearFromQuestion(question, fallbackMonthYear) {
  const text = normalizeForMatch(question);
  const now = new Date();

  if (/\blast month\b/.test(text)) {
    const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return {
      month: previous.getMonth() + 1,
      year: previous.getFullYear(),
    };
  }

  const explicitYear = text.match(/\b(20\d{2}|21\d{2}|22\d{2})\b/);
  const monthName = Object.keys(MONTH_NAME_TO_NUMBER).find((name) => new RegExp(`\\b${name}\\b`).test(text));

  if (monthName) {
    return {
      month: MONTH_NAME_TO_NUMBER[monthName],
      year: explicitYear ? Number(explicitYear[1]) : fallbackMonthYear.year,
    };
  }

  const numericMonth = text.match(/\b(?:month\s*)?([1-9]|1[0-2])\s*[/-]\s*(20\d{2}|21\d{2}|22\d{2})\b/);
  if (numericMonth) {
    return {
      month: Number(numericMonth[1]),
      year: Number(numericMonth[2]),
    };
  }

  return fallbackMonthYear;
}

function buildUnsupportedMessage() {
  return 'I can help with revenue, appointments, patient history, low stock, consultant dues, attendance, monthly reports, and overdue EMI. Please ask one of those.';
}

function parseTypedQuestion(question, monthYear, supportedIntentSet) {
  const originalQuestion = normalizeQuestionText(question);
  const text = normalizeForMatch(originalQuestion);

  if (!text) {
    return { status: 'empty' };
  }

  let intent = '';

  if (/\b(low stock|stock low|consumables low|restock needed|needs restock|running low|restock)\b/.test(text)) {
    intent = 'low_stock';
  } else if (/\b(overdue emi|pending emi|emi due|emi overdue|emi payments overdue|emi payment overdue)\b/.test(text)) {
    intent = 'overdue_emi';
  } else if (/\b(monthly report|report status|report ready|reports ready|monthly reports)\b/.test(text)) {
    intent = 'monthly_report_status';
  } else if (/\b(attendance|absent|staff absent|who was absent|late staff|missing checkout)\b/.test(text)) {
    intent = 'monthly_attendance';
  } else if (/\b(consultant balance|consultant fees|consultant dues|pay dr|pay doctor|owe dr|owe doctor|dues for dr|fees for dr)\b/.test(text)) {
    intent = 'consultant_balance';
  } else if (/\b(last visit|last time|patient history|what did we do|previous visit)\b/.test(text)) {
    intent = 'patient_last_visit';
  } else if (/\b(next appointment|upcoming appointment|patient appointment|appointment)\b/.test(text)) {
    intent = 'patient_next_appointment';
  } else if (/\b(revenue today|today revenue|collection today|today collection|billing today|billed today|collected today|income today)\b/.test(text)) {
    intent = 'today_revenue';
  } else if (/\b(revenue|collection|billing|billed|collected|income)\b/.test(text) && /\btoday\b/.test(text)) {
    intent = 'today_revenue';
  }

  if (!intent && supportedIntentSet.has('software_help')) {
    return {
      status: 'ready',
      intent: 'software_help',
      payload: {
        intent: 'software_help',
        queryText: originalQuestion,
      },
      originalQuestion,
    };
  }

  if (!intent || !supportedIntentSet.has(intent)) {
    return { status: 'unsupported', originalQuestion };
  }

  const payload = {
    intent,
    queryText: originalQuestion,
  };

  if (intent === 'patient_next_appointment' || intent === 'patient_last_visit') {
    const patientSearch = extractPatientSearch(originalQuestion, intent);
    if (!patientSearch) {
      return {
        status: 'follow_up_required',
        originalQuestion,
        followUp: {
          intent,
          field: 'patientSearch',
          message: 'Which patient should I search for?',
          basePayload: payload,
        },
      };
    }
    payload.patientSearch = patientSearch;
  }

  if (intent === 'consultant_balance') {
    const consultantSearch = extractConsultantSearch(originalQuestion);
    if (!consultantSearch) {
      return {
        status: 'follow_up_required',
        originalQuestion,
        followUp: {
          intent,
          field: 'consultantSearch',
          message: 'Which consultant should I check?',
          basePayload: payload,
        },
      };
    }
    payload.consultantSearch = consultantSearch;
  }

  if (intent === 'monthly_attendance' || intent === 'monthly_report_status') {
    const parsedMonthYear = getMonthYearFromQuestion(originalQuestion, monthYear);
    payload.month = parsedMonthYear.month;
    payload.year = parsedMonthYear.year;
  }

  return {
    status: 'ready',
    intent,
    payload,
    originalQuestion,
  };
}

function getFriendlyErrorMessage(error) {
  const status = error?.response?.status;
  const backendMessage = error?.response?.data?.error || '';

  if (status === 423) {
    return 'Finance Lock is enabled. Please unlock Finance Lock to view this answer.';
  }
  if (status === 403) {
    return backendMessage || 'Access restricted for this question.';
  }
  if (status === 400) {
    return backendMessage || 'Please check the question and try again.';
  }
  if (status === 401) {
    return 'Your session expired. Please login again.';
  }
  return backendMessage || 'Dektal is unavailable right now. Please try again.';
}

function isFinanceLockedResponse(response) {
  return (
    response?.answerStatus === 'locked'
    || response?.status === 'locked'
    || (
      response?.answerStatus === 'restricted'
      && (response?.requiresFinanceUnlock || response?.status === 'locked')
    )
  );
}

function makeMessageId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeAssistantResponse(response, intent, payload) {
  const financeLocked = isFinanceLockedResponse(response);
  const multipleMatches = Array.isArray(response?.multipleMatches)
    ? response.multipleMatches
    : Array.isArray(response?.data?.multipleMatches)
      ? response.data.multipleMatches
      : [];

  return {
    sender: 'dektal',
    text: financeLocked
      ? 'Finance Lock is enabled. Please unlock Finance Lock to view this answer.'
      : response?.message || 'No matching record found.',
    summary: response?.summary || '',
    actionButtons: Array.isArray(response?.actionButtons) ? response.actionButtons : [],
    multipleMatches,
    answerStatus: financeLocked ? 'locked' : response?.answerStatus || 'answered',
    data: response?.data || null,
    missingSources: Array.isArray(response?.missingSources) ? response.missingSources : [],
    sourceModulesUsed: Array.isArray(response?.sourceModulesUsed) ? response.sourceModulesUsed : [],
    intent: response?.intent || intent,
    requestPayload: payload,
  };
}

export default function DektalChatPanel({ onNavigateAction }) {
  const monthYear = useMemo(() => buildInitialMonthYear(), []);
  const [messages, setMessages] = useState([buildWelcomeMessage()]);
  const [loading, setLoading] = useState(false);
  const [supportedIntentSet, setSupportedIntentSet] = useState(
    new Set(SUPPORTED_QUESTIONS.map((entry) => entry.intent))
  );
  const [questionText, setQuestionText] = useState('');
  const [pendingFollowUp, setPendingFollowUp] = useState(null);
  const [supportsError, setSupportsError] = useState('');
  const [showExamples, setShowExamples] = useState(false);
  const messagesEndRef = useRef(null);
  const conversationRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const questionInputRef = useRef(null);
  const sendingRef = useRef(false);

  useEffect(() => {
    if (shouldAutoScrollRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, loading]);

  useEffect(() => {
    let mounted = true;

    const loadCapabilities = async () => {
      try {
        const payload = await getAssistantCapabilities();
        const supportedIntents = Array.isArray(payload?.supportedIntents)
          ? payload.supportedIntents.map((entry) => entry?.intent).filter(Boolean)
          : [];

        if (!mounted) return;

        if (supportedIntents.length > 0) {
          setSupportedIntentSet(new Set(supportedIntents));
        }
        setSupportsError('');
      } catch (error) {
        if (!mounted) return;
        setSupportsError(
          error?.response?.data?.error
            || 'Could not load assistant capabilities. Controlled local intent list is being used.'
        );
      }
    };

    loadCapabilities();

    return () => {
      mounted = false;
    };
  }, []);

  const visibleQuestions = useMemo(
    () => SUPPORTED_QUESTIONS.filter((entry) => supportedIntentSet.has(entry.intent)),
    [supportedIntentSet]
  );
  const showStarterChips = messages.length === 1 && messages[0]?.sender === 'dektal' && !pendingFollowUp;

  const appendMessage = (message) => {
    setMessages((current) => [
      ...current,
      {
        id: makeMessageId(message.sender || 'msg'),
        sender: message.sender || 'dektal',
        text: message.text || '',
        summary: message.summary || '',
        actionButtons: Array.isArray(message.actionButtons) ? message.actionButtons : [],
        multipleMatches: Array.isArray(message.multipleMatches) ? message.multipleMatches : [],
        answerStatus: message.answerStatus || 'answered',
        data: message.data || null,
        missingSources: Array.isArray(message.missingSources) ? message.missingSources : [],
        sourceModulesUsed: Array.isArray(message.sourceModulesUsed) ? message.sourceModulesUsed : [],
        intent: message.intent || null,
        requestPayload: message.requestPayload || null,
        createdAt: message.createdAt || new Date().toISOString(),
      },
    ]);
  };

  const appendUserMessage = (text, intent = null, requestPayload = null) => {
    appendMessage({
      sender: 'user',
      text,
      intent,
      requestPayload,
    });
  };

  const appendDektalMessage = (message) => {
    appendMessage({
      sender: 'dektal',
      ...message,
    });
  };

  const runAssistantQuery = async ({
    intent,
    payload,
    userPromptText,
  }) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    const submittedText = userPromptText || payload.queryText || getIntentLabel(intent);
    appendUserMessage(submittedText, intent, payload);
    setLoading(true);
    try {
      const response = await queryAssistant(payload);
      appendDektalMessage(normalizeAssistantResponse(response, intent, payload));
    } catch (error) {
      const errorMessage = getFriendlyErrorMessage(error);
      appendDektalMessage({
        text: errorMessage,
        summary: 'Request failed',
        answerStatus: 'error',
        intent,
        requestPayload: payload,
      });
      toast.error(errorMessage);
      setQuestionText(submittedText);
    } finally {
      sendingRef.current = false;
      setLoading(false);
    }
  };

  const submitQuestion = async (event) => {
    event?.preventDefault?.();
    if (loading || sendingRef.current) return;

    const typedQuestion = normalizeQuestionText(questionText);
    if (!typedQuestion) {
      toast.error('Please type a question for Dektal.');
      questionInputRef.current?.focus();
      return;
    }

    setQuestionText('');

    if (pendingFollowUp) {
      const payload = {
        ...pendingFollowUp.basePayload,
        [pendingFollowUp.field]: typedQuestion,
      };
      setPendingFollowUp(null);
      await runAssistantQuery({
        intent: pendingFollowUp.intent,
        payload,
        userPromptText: typedQuestion,
      });
      return;
    }

    const parsed = parseTypedQuestion(typedQuestion, monthYear, supportedIntentSet);

    if (parsed.status === 'unsupported') {
      appendUserMessage(typedQuestion, null, null);
      appendDektalMessage({
        text: buildUnsupportedMessage(),
        summary: 'Supported question areas only',
        answerStatus: 'unsupported_query',
        data: {
          supportedIntents: visibleQuestions.map((entry) => entry.intent),
        },
      });
      return;
    }

    if (parsed.status === 'follow_up_required') {
      appendUserMessage(typedQuestion, parsed.followUp.intent, parsed.followUp.basePayload);
      setPendingFollowUp(parsed.followUp);
      appendDektalMessage({
        text: parsed.followUp.message,
        summary: 'I need one more detail before checking.',
        answerStatus: 'follow_up_required',
        intent: parsed.followUp.intent,
        requestPayload: parsed.followUp.basePayload,
      });
      questionInputRef.current?.focus();
      return;
    }

    if (parsed.status === 'ready') {
      await runAssistantQuery({
        intent: parsed.intent,
        payload: parsed.payload,
        userPromptText: typedQuestion,
      });
    }
  };

  const handleExampleSelect = (exampleText) => {
    setQuestionText(exampleText);
    setShowExamples(false);
    window.setTimeout(() => {
      questionInputRef.current?.focus();
    }, 0);
  };

  const handleHelpStarterSelect = async (exampleText) => {
    if (loading) return;
    setQuestionText('');
    setShowExamples(false);
    setPendingFollowUp(null);
    await runAssistantQuery({
      intent: 'software_help',
      payload: {
        intent: 'software_help',
        queryText: exampleText,
      },
      userPromptText: exampleText,
    });
  };

  const handleMultipleMatchSelect = async (message, match) => {
    if (loading) return;
    const originalIntent = message?.intent;
    const requestPayload = message?.requestPayload || {};
    if (!originalIntent) return;

    if (originalIntent === 'patient_next_appointment' || originalIntent === 'patient_last_visit') {
      const patientId = match?.patientRecordId || match?.patientId || match?.id;
      if (!patientId) return;
      const payload = {
        ...requestPayload,
        intent: originalIntent,
        patientId: Number(patientId),
      };
      delete payload.patientSearch;
      await runAssistantQuery({
        intent: originalIntent,
        payload,
        userPromptText: `Selected patient: ${match.patientName || match.customerId || `#${patientId}`}`,
      });
      return;
    }

    if (originalIntent === 'consultant_balance') {
      const consultantName = match?.consultantName || match?.name || '';
      if (!consultantName) return;
      const payload = {
        ...requestPayload,
        intent: originalIntent,
        consultantSearch: String(consultantName),
      };
      await runAssistantQuery({
        intent: originalIntent,
        payload,
        userPromptText: `Selected consultant: ${consultantName}`,
      });
    }
  };

  const renderMetadataHint = (message) => {
    if (!message?.missingSources?.length && !message?.sourceModulesUsed?.length) {
      return null;
    }

    return (
      <div className="mt-2 text-[11px] text-slate-400">
        {message.sourceModulesUsed?.length ? (
          <p>Sources: {message.sourceModulesUsed.join(', ')}</p>
        ) : null}
        {message.missingSources?.length ? (
          <p className="mt-0.5 text-amber-600">Missing: {message.missingSources.join(', ')}</p>
        ) : null}
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#f4f7fb]">
      <div ref={conversationRef} onScroll={(event) => { const node = event.currentTarget; shouldAutoScrollRef.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80; }} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4 sm:px-4">
        <div className="mx-auto flex min-h-full max-w-3xl flex-col gap-3">
          {messages.map((message) => {
            const isUser = message.sender === 'user';
            return (
              <div key={message.id} className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex max-w-[92%] items-end gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
                  {!isUser ? (
                    <img
                      src={dektalMascot}
                      alt=""
                      className="mb-1 h-8 w-8 shrink-0 rounded-full border border-white bg-white object-cover shadow-sm"
                    />
                  ) : null}
                  <div className={`flex min-w-0 flex-col ${isUser ? 'items-end' : 'items-start'}`}>
                    <div
                      className={`relative max-w-full px-4 py-2.5 text-sm leading-6 shadow-sm ${
                        isUser
                          ? 'rounded-[22px] rounded-br-md bg-blue-600 text-white'
                          : 'rounded-[22px] rounded-bl-md border border-slate-200 bg-white text-slate-800'
                      }`}
                    >
                      <p className="whitespace-pre-line break-words">{message.text}</p>
                      {message.summary ? (
                        <p className={`mt-1 whitespace-pre-line text-xs ${isUser ? 'text-blue-100' : 'text-slate-500'}`}>
                          {message.summary}
                        </p>
                      ) : null}
                    </div>

                    {message.multipleMatches?.length ? (
                      <div className="mt-2 max-w-full rounded-2xl border border-blue-100 bg-blue-50/90 p-2 shadow-sm">
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-blue-700">
                          Choose one result
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {message.multipleMatches.map((match, index) => {
                            const label = match.patientName || match.consultantName || match.customerId || `Option ${index + 1}`;
                            const subtitle = match.phoneMasked || match.specialization || match.customerId || '';
                            return (
                              <button
                                key={`${message.id}-match-${index}`}
                                type="button"
                                onClick={() => handleMultipleMatchSelect(message, match)}
                                className="rounded-xl border border-blue-200 bg-white px-3 py-2 text-left text-xs font-semibold text-blue-700 transition hover:bg-blue-100"
                              >
                                <p>{label}</p>
                                {subtitle ? <p className="mt-0.5 text-[10px] text-blue-500">{subtitle}</p> : null}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}

                    {message.actionButtons?.length ? (
                      <div className="mt-2 flex max-w-full flex-wrap gap-2">
                        {message.actionButtons.map((button, index) => (
                          <button
                            key={`${message.id}-action-${index}`}
                            type="button"
                            onClick={() => onNavigateAction?.(button, { source: 'assistant', message })}
                            className="rounded-full border border-cyan-200 bg-white px-3 py-1.5 text-xs font-semibold text-cyan-700 shadow-sm transition hover:bg-cyan-50"
                          >
                            {button.label || 'Open'}
                          </button>
                        ))}
                      </div>
                    ) : null}

                    {renderMetadataHint(message)}
                  </div>
                </div>
              </div>
            );
          })}

          {showStarterChips ? (
            <div className="ml-10 rounded-[22px] border border-white bg-white/70 p-3 shadow-sm">
              <p className="mb-2 text-xs font-semibold text-slate-500">Quick questions</p>
              <div className="flex flex-wrap gap-2">
                {HELP_STARTER_QUESTIONS.map((question) => (
                  <button
                    key={question}
                    type="button"
                    onClick={() => handleHelpStarterSelect(question)}
                    disabled={loading}
                    className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-left text-xs font-semibold text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="flex w-full justify-start">
              <div className="flex max-w-[92%] items-end gap-2">
                <img
                  src={dektalMascot}
                  alt=""
                  className="mb-1 h-8 w-8 shrink-0 rounded-full border border-white bg-white object-cover shadow-sm"
                />
                <div className="rounded-[22px] rounded-bl-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
                  <div className="flex items-center gap-1.5" aria-label="Dektal is typing">
                    <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.2s]" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.1s]" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400" />
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <form onSubmit={submitQuestion} className="shrink-0 border-t border-slate-200 bg-white/95 px-3 py-2 shadow-[0_-10px_30px_rgba(15,23,42,0.06)] sm:px-4">
        {supportsError ? (
          <div className="mb-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {supportsError}
          </div>
        ) : null}

        {pendingFollowUp ? (
          <div className="mb-2 rounded-2xl border border-cyan-100 bg-cyan-50/80 px-3 py-2 text-xs text-cyan-800">
            Dektal is waiting for: {pendingFollowUp.field === 'patientSearch' ? 'patient name, phone, or customer ID' : 'consultant name'}
          </div>
        ) : null}

        {showExamples ? (
          <div className="mb-2 max-h-40 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-2">
            <div className="grid grid-cols-1 gap-1.5">
              {visibleQuestions.map((question) => (
                <div key={question.intent} className="rounded-xl bg-white px-3 py-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{question.label}</p>
                  <div className="mt-1 space-y-1">
                    {question.examples.map((example) => (
                      <button
                        key={example}
                        type="button"
                        onClick={() => handleExampleSelect(example)}
                        className="block text-left text-xs text-slate-600 transition hover:text-blue-700"
                      >
                        {example}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex items-end gap-2 rounded-[28px] bg-slate-100 p-1.5">
          <button
            type="button"
            onClick={() => setShowExamples((current) => !current)}
            className="mb-0.5 inline-flex h-9 shrink-0 items-center gap-1 rounded-full px-3 text-xs font-semibold text-slate-500 transition hover:bg-white hover:text-blue-700"
          >
            Help
            <ChevronDown className={`h-3.5 w-3.5 transition ${showExamples ? 'rotate-180' : ''}`} />
          </button>
          <label className="min-w-0 flex-1">
            <span className="sr-only">Ask Dektal</span>
            <textarea
              ref={questionInputRef}
              value={questionText}
              onChange={(event) => setQuestionText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  submitQuestion(event);
                }
              }}
              rows={1}
              placeholder={pendingFollowUp ? 'Type the name here...' : 'Ask how to use Dektal 360 Pro…'}
              className="max-h-24 min-h-[40px] w-full resize-none bg-transparent px-1 py-2.5 text-sm leading-5 text-slate-800 outline-none placeholder:text-slate-400"
            />
          </label>
          <button
            type="submit"
            disabled={loading || !normalizeQuestionText(questionText)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white shadow-[0_8px_18px_rgba(37,99,235,0.28)] transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            title="Ask Dektal"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </form>
    </div>
  );
}
